import { type ReactNode, useCallback, useEffect, useMemo, useState } from 'react';
import type { PhoneHost } from '../../embed/phoneState';
import { useComms } from './context';
import { setCallLive } from './liveCallFlag';
import { PhoneContext } from './phone';
import { useHostPhone } from './useHostPhone';
import { useTwilioPhone } from './useTwilioPhone';

/**
 * Holds the module's Twilio softphone — or the remote control for the
 * dashboard's, which is the same thing from every consumer's side.
 *
 * Mounted inside CommsProvider because both read the inbox list and the
 * agent's calling grants from it, and above the views so a call survives moving
 * between tabs. Which one runs is settled by the source and never changes for
 * its lifetime, so the split is two components rather than a branch inside one:
 * a hook cannot be called conditionally, and a device that got torn down and
 * rebuilt on a re-render would drop the call it was holding.
 */
export function PhoneProvider({ children }: { children: ReactNode }) {
  const { source } = useComms();
  return source.phoneHost ? (
    <HostedPhone host={source.phoneHost}>{children}</HostedPhone>
  ) : (
    <OwnPhone>{children}</OwnPhone>
  );
}

/**
 * The console's own device — the standalone console, and any older dashboard.
 *
 * The keypad panel's open/closed state lives here rather than in the device
 * hook: the hook is shared with the softphone frame (`src/phone/PhoneApp.tsx`),
 * which is its whole page and has no panel to open.
 */
function OwnPhone({ children }: { children: ReactNode }) {
  const { source, inboxes, canCall } = useComms();
  // Stable for the source's lifetime, like the token minter: the device hook
  // keys its effects on these.
  const controls = useMemo(
    () => ({
      setRecording: source.api.setCallRecording,
      transfer: source.api.transferCall,
      addParticipant: source.api.addCallParticipant,
      listParticipants: source.api.listCallParticipants,
      removeParticipant: source.api.removeCallParticipant,
    }),
    [source],
  );
  const device = useTwilioPhone({ inboxes, canCall, getVoiceToken: source.api.getVoiceToken, controls });
  const [panelOpen, setPanelOpen] = useState(false);

  // The pad is for STARTING a call. Once one is ringing or up, the call bar
  // has the controls, and a keypad left open beside it reads as a second phone
  // — so a call starting closes it, and it stays closed when the call ends.
  // State adjusted during render on the transition rather than in an effect:
  // the panel must be gone in the same frame the call bar appears.
  const busy = device.status === 'ringing' || device.status === 'active';
  // What the route around this module asks before it unmounts us: a device
  // destroyed mid-call disconnects the call. See liveCallFlag.
  useEffect(() => setCallLive(busy), [busy]);
  useEffect(() => () => setCallLive(false), []);
  const [wasBusy, setWasBusy] = useState(busy);
  if (busy !== wasBusy) {
    setWasBusy(busy);
    if (busy) setPanelOpen(false);
  }

  const showPanel = useCallback(() => setPanelOpen(true), []);
  const hidePanel = useCallback(() => setPanelOpen(false), []);

  const phone = useMemo(
    () => ({ ...device, panelOpen: panelOpen && !busy, showPanel, hidePanel }),
    [device, panelOpen, busy, showPanel, hidePanel],
  );
  return <PhoneContext.Provider value={phone}>{children}</PhoneContext.Provider>;
}

/** The dashboard's, held in a frame that outlives this module (see useHostPhone). */
function HostedPhone({ host, children }: { host: PhoneHost; children: ReactNode }) {
  const { inboxes } = useComms();
  const phone = useHostPhone(host, inboxes);
  return <PhoneContext.Provider value={phone}>{children}</PhoneContext.Provider>;
}
